/**
 * Certificate of completion — the office's side of the customer's page
 * (ADR 0308).
 *
 * Opened from Docs on the job. The certificate is filled in from the job —
 * the customer's name, the property address, what kind of work it was and
 * the day the last work order was ticked complete — and the office checks
 * it, presses Create, and chooses how it goes out: email it from here, copy
 * the link into a text, or hand the phone over. The same three ways an
 * estimate and a change order go out, because Jonathon asked for those to be
 * the same and this is the same kind of thing.
 *
 * Jonathon, 2026-10-24: "it needs to be super easy to use." So the form is
 * four fields already filled, and the usual press is Create then Email.
 */

import {
  PROJECT_TYPES,
  buildCompletionCertificate,
  certificateInviteLetter,
  certificateLink,
  certificateProblems,
  jobAddress,
  latestCompletion,
  suggestProjectType,
  type CompletionCertificate,
  type Job,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  fetchCompanySettings,
  fetchEstimates,
  fetchJobWorkOrders,
  presentCompletionCertificate,
} from "../../lib/queries";
import { ComposeEmail } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";
import { CertificateView } from "../present/CertificatePage";

/** A day as the office's own device reckons it. Never built through `new Date(string)`. */
function localDate(at: Date): string {
  const month = `${at.getMonth() + 1}`.padStart(2, "0");
  const day = `${at.getDate()}`.padStart(2, "0");
  return `${at.getFullYear()}-${month}-${day}`;
}

const OTHER = "__other__";

/**
 * The kind of work and the day it was finished, from the job's own answers.
 * Filled in once they arrive and left alone after that: what somebody has
 * typed is never overwritten by a late fetch.
 */
function useJobDefaults(job: Job) {
  const workOrders = useAsync(() => fetchJobWorkOrders(job.id), [job.id]);
  const estimates = useAsync(() => fetchEstimates(job.id), [job.id]);
  const [projectType, setProjectType] = useState<string>("");
  const [dateCompleted, setDateCompleted] = useState("");

  useEffect(() => {
    if (dateCompleted || workOrders.loading) return;
    const latest = latestCompletion(workOrders.data ?? []);
    setDateCompleted(localDate(latest ? new Date(latest) : new Date()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders.loading]);

  useEffect(() => {
    if (projectType || estimates.loading) return;
    const all = estimates.data ?? [];
    const active = all.filter((e) => e.is_active);
    setProjectType(
      suggestProjectType({
        bidType: job.bid_type,
        trades: (active.length ? active : all).map((e) => e.trade),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimates.loading]);

  return { projectType, setProjectType, dateCompleted, setDateCompleted };
}

/** Whether the link can be emailed from here, and if not, why. */
function emailAvailability(gmail: {
  account: { email_address: string } | null;
  loading: boolean;
  error: string | null;
}): { address: string | null; canEmail: boolean; whyNot: string | null } {
  const address = gmail.account?.email_address ?? null;
  if (gmail.loading)
    return {
      address,
      canEmail: false,
      whyNot: t("Checking your email connection…"),
    };
  if (gmail.error) return { address, canEmail: false, whyNot: gmail.error };
  if (!address) {
    return {
      address,
      canEmail: false,
      whyNot: t("Connect your Google account in Tools to email from here."),
    };
  }
  return { address, canEmail: true, whyNot: null };
}

export function CertificateModal({
  job,
  onClose,
  onCreated,
}: {
  job: Job;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const company = useAsync(fetchCompanySettings, []);
  const defaults = useJobDefaults(job);
  const [homeowner, setHomeowner] = useState(job.contact?.full_name ?? "");
  const [address, setAddress] = useState(jobAddress(job));
  const [otherType, setOtherType] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const { projectType, setProjectType, dateCompleted, setDateCompleted } =
    defaults;

  const draft = buildCompletionCertificate({
    company: company.data,
    homeowner,
    propertyAddress: address,
    projectType: projectType === OTHER ? otherType : projectType,
    dateCompleted,
    issuedOn: localDate(new Date()),
    jobNumber: job.job_number,
    rep: job.rep
      ? { name: job.rep.full_name, phone: job.rep.phone ?? null }
      : null,
  });
  const problems = certificateProblems(draft);
  const waiting = busy || company.loading;

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const made = await presentCompletionCertificate({
        jobId: job.id,
        snapshot: draft,
      });
      setLink(certificateLink(window.location.origin, made.token));
      await onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (previewing) {
    return (
      <Modal
        title={t("What the customer will see")}
        onClose={() => setPreviewing(false)}
        wide
      >
        <CertificateView
          document={draft}
          signature={null}
          readOnly
          onSign={() => undefined}
        />
      </Modal>
    );
  }

  if (link) {
    return (
      <CertificateReady job={job} draft={draft} link={link} onClose={onClose} />
    );
  }

  return (
    <Modal title={t("Certificate of completion")} onClose={onClose}>
      <p className="n">
        {job.job_number
          ? `${t("Job {number}", { number: job.job_number })} · `
          : ""}
        {job.contact?.full_name ?? jobAddress(job)}
      </p>
      <p>
        {t(
          "Check these four things, then create the certificate and send it. The customer signs it on their phone.",
        )}
      </p>

      <CertificateFields
        homeowner={homeowner}
        setHomeowner={setHomeowner}
        address={address}
        setAddress={setAddress}
        projectType={projectType}
        setProjectType={setProjectType}
        otherType={otherType}
        setOtherType={setOtherType}
        dateCompleted={dateCompleted}
        setDateCompleted={setDateCompleted}
      />

      {problems.length > 0 ? (
        <p className="warn">{problems.map((p) => t(p)).join(" ")}</p>
      ) : null}
      <ErrorNote error={error} />

      <div className="mact">
        <button type="button" onClick={() => setPreviewing(true)}>
          {t("Preview")}
        </button>
        <button
          type="button"
          className="primary"
          disabled={waiting || problems.length > 0}
          onClick={() => void create()}
        >
          {waiting ? t("One moment…") : t("Create")}
        </button>
      </div>
      <p className="n">
        {t(
          "Creating locks what the customer reads and makes their link. It does not send anything — you choose how it goes out next. A certificate already sent on this job and not yet signed is withdrawn.",
        )}
      </p>
    </Modal>
  );
}

/** The four things to check before Create. */
function CertificateFields(props: {
  homeowner: string;
  setHomeowner: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  projectType: string;
  setProjectType: (value: string) => void;
  otherType: string;
  setOtherType: (value: string) => void;
  dateCompleted: string;
  setDateCompleted: (value: string) => void;
}) {
  return (
    <>
      <Row label={t("Homeowner")}>
        <input
          type="text"
          value={props.homeowner}
          onChange={(e) => props.setHomeowner(e.target.value)}
        />
      </Row>
      <Row label={t("Property address")}>
        <input
          type="text"
          value={props.address}
          onChange={(e) => props.setAddress(e.target.value)}
        />
      </Row>
      <Row label={t("Project type")}>
        <select
          value={props.projectType}
          onChange={(e) => props.setProjectType(e.target.value)}
          aria-label={t("Project type")}
        >
          {PROJECT_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(type)}
            </option>
          ))}
          <option value={OTHER}>{t("Something else…")}</option>
        </select>
        {props.projectType === OTHER ? (
          <input
            type="text"
            placeholder={t("What kind of work it was")}
            value={props.otherType}
            onChange={(e) => props.setOtherType(e.target.value)}
          />
        ) : null}
      </Row>
      <Row label={t("Date completed")}>
        <input
          type="date"
          value={props.dateCompleted}
          onChange={(e) => props.setDateCompleted(e.target.value)}
          aria-label={t("Date completed")}
        />
      </Row>
    </>
  );
}

/**
 * The link is live: copy it, email it, or hand the phone over. Emailing
 * needs the sender's own Gmail connected, like every letter the app sends.
 */
function CertificateReady({
  job,
  draft,
  link,
  onClose,
}: {
  job: Job;
  draft: CompletionCertificate;
  link: string;
  onClose: () => void;
}) {
  const gmail = useGmailAccount();
  const senders = useGmailSenders();
  const [emailing, setEmailing] = useState(false);

  const {
    address: gmailAddress,
    canEmail,
    whyNot: whyNoEmail,
  } = emailAvailability(gmail);
  const who = draft.homeowner || t("the customer");
  const invite = certificateInviteLetter({
    homeowner: draft.homeowner,
    propertyAddress: draft.propertyAddress,
    projectType: draft.projectType,
    companyName: draft.company.name,
    link,
    senderName: job.rep?.full_name ?? null,
    senderPhone: job.rep?.phone ?? null,
  });

  return (
    <Modal title={t("Ready to sign")} onClose={onClose}>
      <p>
        {t(
          "The certificate is locked and the link is live. Once {who} signs it, the signed PDF lands in Docs here and is emailed to them and to the rep.",
          { who },
        )}
      </p>
      <Row label={t("Link")}>
        <input readOnly value={link} onFocus={(e) => e.target.select()} />
      </Row>
      <div className="mact">
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(link)}
        >
          {t("Copy link")}
        </button>
        <button
          type="button"
          disabled={!canEmail}
          title={whyNoEmail ?? undefined}
          onClick={() => setEmailing(true)}
        >
          {t("Email the link")}
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => window.location.assign(link)}
        >
          {t("Sign on this device")}
        </button>
      </div>
      <p className="n">
        {canEmail
          ? t(
              "Email it below, or paste the link wherever you already talk to this customer.",
            )
          : `${whyNoEmail} ${t("Until then, paste the link wherever you already talk to this customer.")}`}
      </p>
      {emailing ? (
        <ComposeEmail
          title={t("Email the certificate to {who}", { who })}
          subtitle={t("They read it and sign it on their phone.")}
          to={job.contact?.email ?? ""}
          subject={invite.subject}
          body={invite.text}
          contact={job.contact}
          contactId={job.contact_id}
          jobId={job.id}
          connectedAs={gmailAddress}
          senders={senders.senders}
          ownSender={senders.own}
          onClose={() => setEmailing(false)}
          onSent={onClose}
        />
      ) : null}
    </Modal>
  );
}
